import { CalendarMonth } from "@mui/icons-material";
import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { PencilIcon } from "@heroicons/react/24/outline";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DataRetentionPolicyCategoryDto,
  DataRetentionPolicyDetailCategory,
  RetentionOptions,
  UpdateDataRetentionPolicyDetailDto,
  CreateDataRetentionPolicyDetailDto,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";
import { PICCheckboxes } from "../../get-compliant/collection-groups/CollectionCheckboxCallout";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { ConfirmDialog } from "../../dialog";
import { SelectMenuStandard } from "@truevault/aquilo";

type DataRetentionCategoryQuestionProps = {
  detail: DataRetentionPolicyCategoryDto | null;
  picOptions: CategoryDto[];
  onUpdate: (dto: UpdateDataRetentionPolicyDetailDto) => void;

  onRemove?: () => void;
  setApproved?: (retentionKey: string, approved: boolean) => void;
  onCreate?: (dto: CreateDataRetentionPolicyDetailDto) => void;
  onCreated?: () => void;
};

export const DataRetentionCategoryQuestion: React.FC<DataRetentionCategoryQuestionProps> = ({
  detail,
  picOptions,
  onUpdate,
  onCreate,
  onCreated,
  onRemove,
  setApproved: setDetailApproved,
}) => {
  const isAdd = detail == null;

  const [approved, setApproved] = useState(detail?.approved ?? false);
  const [hasUpdated, setHasUpdated] = useState(false);
  const [selectedPic, setSelectedPic] = useState(detail?.pic ?? []);

  let startingDescription = detail?.description ?? "";
  if (startingDescription === "" && DATA_RETENTION_CATEGORIES[detail?.builtinType]?.description) {
    startingDescription = DATA_RETENTION_CATEGORIES[detail?.builtinType]?.description;
  }
  const [description, setDescription] = useState(startingDescription);
  const [lastCustom, setLastCustom] = useState(detail?.description ?? "");
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const [label, setLabel] = useState(labelForDetail(detail));
  const type = detail?.builtinType;
  const formattedLabel = formattedCategoryLabel(type, label);

  const canSetRetention =
    detail?.builtinType != null && builtinDescriptionForType(detail.builtinType).includes("YEARS");
  const defaultRetentionPeriod: RetentionOptions = canSetRetention ? "YEARS_7" : "CUSTOM";
  // Determine initial retention period:
  // - If category can't set retention (no "YEARS" placeholder in template) → "CUSTOM"
  // - If no existing detail → use default ("YEARS_7")
  // - If existing detail has null retention → "CUSTOM" (user cleared it)
  // - Otherwise → use saved retention period, falling back to default
  const initialRetentionPeriod: RetentionOptions = canSetRetention
    ? detail
      ? detail.retentionPeriod === null
        ? "CUSTOM"
        : detail.retentionPeriod ?? defaultRetentionPeriod
      : defaultRetentionPeriod
    : "CUSTOM";
  const [retentionPeriod, innerSetRetentionPeriod] =
    useState<RetentionOptions>(initialRetentionPeriod);

  // Track when retention period is changed by user so we still save even if description updates programmatically
  const retentionChangeRef = useRef(false);

  const setRetentionPeriod = (value: RetentionOptions) => {
    retentionChangeRef.current = true;
    innerSetRetentionPeriod((old) => {
      if (old !== value) {
        setApproved(false);
      }
      return value;
    });
    if (value == "CUSTOM") {
      // If we haven't already saved the retention period, and we have no previous custom description, then copy over whatever the current description is to the custom description to make it editable
      if (lastCustom == "" && detail?.retentionPeriod === null) {
        setDescription(description);
      } else {
        setDescription(lastCustom);
      }
    }
    setHasUpdated(true);
  };

  useEffect(() => {
    if (hasUpdated) {
      setApproved(false);
    }
  }, [description]);

  const [suggestedCategories, otherCategories] = useMemo(() => {
    const suggestedKeys = detail?.builtinType
      ? DATA_RETENTION_CATEGORIES[detail.builtinType].suggested
      : [];
    return _.partition(picOptions, (pic) => suggestedKeys.includes(pic.key));
  }, [detail?.builtinType, picOptions]);

  // Track if component has mounted to prevent saves on initial load
  const isMountedRef = useRef(false);
  const isProgrammaticUpdateRef = useRef(false);

  // Use refs to store latest values for the debounced save function
  const onUpdateRef = useRef(onUpdate);
  const onCreateRef = useRef(onCreate);
  const onCreatedRef = useRef(onCreated);
  const isAddRef = useRef(isAdd);
  const detailIdRef = useRef(detail?.id);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
    onCreateRef.current = onCreate;
    onCreatedRef.current = onCreated;
    isAddRef.current = isAdd;
    detailIdRef.current = detail?.id;
  }, [onUpdate, onCreate, onCreated, isAdd, detail?.id]);

  // Mark as mounted after first render
  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  // Track when description is being set programmatically (only when type or retentionPeriod changes)
  useEffect(() => {
    const newDescription = descriptionForTypeAndPeriod(type, retentionPeriod);
    if (newDescription) {
      isProgrammaticUpdateRef.current = true;
      setDescription(newDescription);
      // Reset flag after a tick to allow saves again
      setTimeout(() => {
        isProgrammaticUpdateRef.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, retentionPeriod]);

  // Create stable debounced save function
  const debouncedSaveRef = useRef(
    _.debounce(
      (saveData: {
        pic: string[];
        description: string;
        customLabel: string | null;
        retentionPeriod: RetentionOptions | null;
        approved: boolean;
      }) => {
        if (!isAddRef.current && detailIdRef.current) {
          onUpdateRef.current(saveData);
        }
      },
      750,
    ),
  );

  // Save when any relevant field changes (debounced - waits for inactivity)
  // Only save after mount and when user has made changes (not programmatic updates)
  useEffect(() => {
    const ignoreProgrammaticDueToRetentionChange = retentionChangeRef.current === true;
    if (
      !isMountedRef.current ||
      (isProgrammaticUpdateRef.current && !ignoreProgrammaticDueToRetentionChange)
    ) {
      return;
    }
    if (!hasUpdated) {
      return;
    }

    let isApproved = approved;
    const finalDescription =
      retentionPeriod === "CUSTOM"
        ? description
        : descriptionForTypeAndPeriod(type, retentionPeriod) ?? description;

    if (selectedPic?.length === 0) {
      isApproved = false;
    } else {
      // Auto-approve when all required fields are complete
      const hasValidDescription = finalDescription && finalDescription.trim().length > 0;
      const hasValidLabel = type != null || (label && label.trim().length > 0);

      if (hasValidDescription && hasValidLabel && retentionPeriod) {
        isApproved = true;
      }
    }

    // Update local approved state if it changed
    if (isApproved !== approved) {
      setApproved(isApproved);
    }

    if (isAdd || !detail?.id) {
      return;
    }

    debouncedSaveRef.current({
      pic: selectedPic,
      description: finalDescription,
      customLabel: type == null ? label : null,
      retentionPeriod: retentionPeriod,
      approved: isApproved,
    });

    // Reset the retention change flag after scheduling save
    retentionChangeRef.current = false;
  }, [
    hasUpdated,
    selectedPic,
    description,
    retentionPeriod,
    approved,
    label,
    type,
    isAdd,
    detail?.id,
  ]);

  // Keep latest setDetailApproved in a ref to avoid effect loops due to changing identity
  const setDetailApprovedRef = useRef(setDetailApproved);
  useEffect(() => {
    setDetailApprovedRef.current = setDetailApproved;
  }, [setDetailApproved]);

  // Notify parent when local approved changes
  useEffect(() => {
    if (detail?.id && setDetailApprovedRef.current) {
      setDetailApprovedRef.current(detail.id, approved);
    }
  }, [approved, detail?.id]);

  // Track last custom description only when appropriate
  useEffect(() => {
    if (retentionPeriod == "CUSTOM" && approved && hasUpdated) {
      setLastCustom(description);
    }
  }, [retentionPeriod, approved, hasUpdated, description]);

  useEffect(() => {
    if (isAdd) {
      return; // don't auto-select suggested while adding to avoid unexpected saves/moves
    }
    if (selectedPic.length == 0 && suggestedCategories.length != 0 && !hasUpdated) {
      // auto-select suggested
      const selected = new Set<string>();
      suggestedCategories.forEach((pic) => {
        selected.add(pic.id);
      });
      setSelectedPic([...selected]);
      setHasUpdated(true);
    }
  }, [selectedPic, suggestedCategories, hasUpdated, isAdd]);

  return (
    <div className="mb-lg border-color-neutral-300 box-rounded no-overflow">
      <div className="bg-gray p-md d-flex flex-row flex--between flex--align-center">
        {isAdd && (
          <div>
            <TextField
              placeholder="Add a retention purpose, keep it short and sweet!"
              onChange={(e) => {
                setLabel(e.target.value ?? "");
                setHasUpdated(true);
              }}
              className="min-width-512 max-width-512"
            />
          </div>
        )}
        {!isAdd && (
          <>
            {type == null ? (
              isEditingTitle ? (
                <TextField
                  value={label ?? ""}
                  variant="standard"
                  autoFocus
                  className="min-width-256"
                  onChange={(e) => {
                    setLabel(e.target.value ?? "");
                    setHasUpdated(true);
                  }}
                  onBlur={() => setIsEditingTitle(false)}
                />
              ) : (
                <div
                  className="d-flex flex-row flex--align-center cursor-pointer tw-boundary"
                  onClick={() => setIsEditingTitle(true)}
                  title="Click to edit title"
                >
                  <h6 className="my-0 text-weight-medium mr-sm">
                    {label || "Untitled Retention Purpose"}
                  </h6>
                  <PencilIcon className="h-4 w-4 text-color-primary-500" />
                </div>
              )
            ) : (
              <h6 className="my-0 text-weight-medium">{formattedLabel}</h6>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={false}
                  onChange={({ target }) => {
                    if (target.checked) {
                      setRemoveDialogOpen(true);
                    }
                  }}
                />
              }
              label="Not Applicable"
            />
            <ConfirmDialog
              open={removeDialogOpen}
              onClose={() => {
                setRemoveDialogOpen(false);
              }}
              onConfirm={() => {
                onRemove();
                setRemoveDialogOpen(false);
              }}
              confirmTitle="Are you sure?"
              contentText={`You will lose any collection and retention period data you previously selected.`}
            />
          </>
        )}
      </div>

      <div className="p-md">
        {suggestedCategories.length > 0 && (
          <div className="p-md background-color-primary-100 rounded">
            <h6 className="mb-xs mt-0 text-weight-medium d-flex flex-row flex--align-center">
              <img
                src="/assets/images/surveys/onboarding/suggested-data-icon.svg"
                className="mr-md"
              />
              Suggested Data
            </h6>

            <div className="ml-xl">
              <PICCheckboxes
                categories={suggestedCategories}
                checked={selectedPic}
                onChecked={(id, checked) => {
                  setSelectedPic((old) => {
                    const set = new Set(old);
                    if (checked) {
                      set.add(id);
                    } else {
                      set.delete(id);
                    }
                    return [...set];
                  });
                  setHasUpdated(true);
                }}
                descriptions={false}
                pendingIds={[]}
              />
            </div>
          </div>
        )}

        {otherCategories.length > 0 && (
          <div className="mx-md">
            <h6
              className={`mb-xs text-weight-medium d-flex flex-row flex--align-center ${
                suggestedCategories.length == 0 ? "mt-md" : ""
              }`}
            >
              <img
                src="/assets/images/surveys/onboarding/data-collection-icon.svg"
                className="mr-md"
              />
              Other Data You Collect
            </h6>

            <div className="ml-xl">
              <PICCheckboxes
                categories={otherCategories}
                checked={selectedPic}
                onChecked={(id, checked) => {
                  setSelectedPic((old) => {
                    const set = new Set(old);
                    if (checked) {
                      set.add(id);
                    } else {
                      set.delete(id);
                    }
                    return [...set];
                  });
                  setHasUpdated(true);
                }}
                descriptions={false}
                pendingIds={[]}
              />
            </div>
          </div>
        )}

        <div>
          <h6 className="text-weight-medium mb-sm d-flex flex-row flex--align-center">
            <CalendarMonth color="primary" className="mr-md text-t6 ml-md" />
            How long do you retain this data?
          </h6>
          {type === "SIGN_UP_FOR_PROMOTIONAL_AND_MARKETING" && (
            <div className="ml-xl">
              <div className="text-component-help mb-sm ml-md">
                It is recommended not to retain data longer than 3 years for marketing purposes.
              </div>
            </div>
          )}
          {canSetRetention && (
            <div className="p-md tw-boundary">
              <div className="pl-xl" style={{ maxWidth: "300px" }}>
                <SelectMenuStandard
                  items={[
                    { label: "2 years", value: "YEARS_2" },
                    { label: "5 years", value: "YEARS_5" },
                    { label: "7 years", value: "YEARS_7" },
                    { label: "10 years", value: "YEARS_10" },
                    { label: "Indefinite", value: "INDEFINITE" },
                    { label: "Custom", value: "CUSTOM" },
                  ]}
                  onSelect={(value) => setRetentionPeriod(value as RetentionOptions)}
                  selectedValue={retentionPeriod}
                  placeholder="Select retention period"
                />
              </div>
            </div>
          )}
        </div>

        <div className="pl-xl">
          {Boolean(retentionPeriod) && (
            <>
              {canSetRetention && (
                <h6 className="text-weight-medium d-flex mb-xs flex-row flex--align-center ml-md mt-sm">
                  Retention Period
                </h6>
              )}
              <div className="pr-md">
                {retentionPeriod == "CUSTOM" && (
                  <>
                    {canSetRetention && (
                      <div className="text-component-help mb-sm flex-grow mt-xs ml-md">
                        Describe a retention period for data that is reasonable for the purposes for
                        which the data is used.
                      </div>
                    )}
                    <TextField
                      className="freetext--textfield ml-md"
                      variant="outlined"
                      value={description}
                      fullWidth={true}
                      onChange={(e) => {
                        setHasUpdated(true);
                        setDescription(e.target.value);
                        setApproved(false);
                      }}
                      multiline={true}
                      minRows={8}
                      placeholder="Write your custom retention period here"
                      sx={{
                        "& .MuiInputBase-root": {
                          minHeight: "200px",
                          alignItems: "flex-start",
                        },
                      }}
                    />

                    {!canSetRetention && !detail?.builtinType && (
                      <div className="text-component-help flex-grow mb-md ml-md">
                        Example: We keep data used for [your custom purpose here] for up to [X]
                        years.
                      </div>
                    )}
                  </>
                )}
                {retentionPeriod != "CUSTOM" && canSetRetention && (
                  <p className="text-muted text-t2 flex-grow ml-md">
                    {formattedDescription(description)}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {isAdd && (
        <div className="d-flex justify-end mt-md pr-md pb-md">
          <Button
            variant="outlined"
            className="mr-sm"
            onClick={() => {
              onCreated && onCreated();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!label || label.trim().length === 0}
            onClick={() => {
              if (!onCreate) return;
              onCreate({
                builtinType: null,
                description: description ?? "",
                customLabel: label ?? "",
                retentionPeriod: retentionPeriod,
                pic: selectedPic,
                approved: approved,
              });
              onCreated && onCreated();
            }}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

const labelForDetail = (detail: DataRetentionPolicyCategoryDto | null): string => {
  if (!detail) {
    return "";
  }

  if (detail.builtinType) {
    return DATA_RETENTION_CATEGORIES[detail.builtinType].label;
  }

  return detail.customLabel ?? "";
};

export const formattedCategoryLabel = (
  type: DataRetentionPolicyDetailCategory | null | undefined,
  label: string,
): React.ReactNode => {
  if (!type) {
    return label;
  }
  const category = DATA_RETENTION_CATEGORIES[type];

  const [before, after] = category.label.split(category.strong, 2);
  return (
    <>
      <span className="text-weight-light">{before}</span>
      <span className="text-weight-medium">{category.strong}</span>
      <span className="text-weight-light">{after}</span>
    </>
  );
};

const formattedDescription = (description: string): React.ReactNode => {
  const re = /(\d+) years/i;
  const match = description.match(re);

  // If there's no "X years" pattern, just return the description as-is
  if (!match) {
    return description;
  }

  const all = description.split(re, 3);
  const [before, capture, after] = all;

  return (
    <>
      {before}
      <span className="text-color-primary text-weight-medium">{capture} years</span>
      {after}
    </>
  );
};

const builtinDescriptionForType = (type: DataRetentionPolicyDetailCategory): string => {
  return DATA_RETENTION_CATEGORIES[type].description;
};

export const descriptionForTypeAndPeriod = (
  type: DataRetentionPolicyDetailCategory | undefined,
  period: RetentionOptions,
): string | null => {
  if (!type) {
    return null;
  }

  const template = DATA_RETENTION_CATEGORIES[type]?.description;
  if (!template) {
    return null;
  }

  // Special handling for INDEFINITE - use a fixed message and append the trailing text
  if (period === "INDEFINITE") {
    let baseMessage =
      "We keep as long as necessary to fulfill the purpose for which we collect it.";

    // Extract any text that comes after the "YEARS" placeholder in the template
    // This includes additional clauses like "We may keep data beyond this period..."
    if (template.includes("YEARS")) {
      const parts = template.split("YEARS");
      if (parts.length > 1) {
        // Get the text after "YEARS" and clean up the beginning
        let trailingText = parts[1];
        // Remove common time-related prefixes like "of collecting it", "from your last purchase with us", etc.
        trailingText = trailingText
          .replace(/^[^.]*?\.\s*/, " ") // Remove everything up to and including the first period, then add space
          .trim();

        if (trailingText) {
          baseMessage += " " + trailingText;
        }
      }
    }

    return baseMessage;
  }

  const replacement = yearsForPeriod(period);
  if (replacement == null) {
    return null;
  }

  return template.replace("YEARS", replacement);
};

const yearsForPeriod = (period: RetentionOptions): string | null => {
  switch (period) {
    case "YEARS_2":
      return "2 years";
    case "YEARS_5":
      return "5 years";
    case "YEARS_7":
      return "7 years";
    case "YEARS_10":
      return "10 years";
    case "INDEFINITE":
      return "indefinitely";
    case "CUSTOM":
      return null;
  }
};

export const getDefaultDescription = (detail: DataRetentionPolicyCategoryDto): string | null => {
  if (!detail.builtinType) return null;
  const template = DATA_RETENTION_CATEGORIES[detail.builtinType]?.description;
  if (!template) return null;
  return descriptionForTypeAndPeriod(detail.builtinType, detail.retentionPeriod) ?? template;
};

type BuiltinPolicy = {
  label: string;
  strong: string;
  description: string;
  suggested: string[];
  displayOrder: number;
};

export const DATA_RETENTION_CATEGORIES: Record<DataRetentionPolicyDetailCategory, BuiltinPolicy> = {
  ONLINE_DATA_FROM_WEBSITE: {
    label: "Cookies and other online data we collect while you use our website",
    strong: "Cookies and other online data",
    description:
      "We delete or anonymize data concerning your use of our website within YEARS of collecting it. Cookies and on-device data can be deleted at any time by you, the consumer.",
    suggested: ["online-identifiers", "internet-activity", "geolocation-information"],
    displayOrder: 1,
  },
  PROCESS_AND_SHIP_ORDERS: {
    label: "Data we collect in order to process and ship orders you place with us",
    strong: "process and ship orders",
    description:
      "We keep personal information related to products and services you purchase for as long as the personal data is required for us to fulfill our contract with you, and for YEARS from your last purchase with us. We may keep data beyond this period in anonymized form.",
    suggested: [
      "name",
      "email-address",
      "postal-address",
      "purchases",
      "phone-number",
      "credit-debit-card-number",
    ],
    displayOrder: 2,
  },
  CONTACT_CUSTOMER_SUPPORT: {
    label: "Data we collect when you contact us for customer support and other inquiries",
    strong: "customer support and other inquiries",
    description:
      "We keep customer feedback and correspondence with our customer service for up to YEARS to help us respond to any questions or complaints. We may keep data beyond this period in anonymized form.",
    suggested: [
      "name",
      "email-address",
      "purchases",
      "phone-number",
      "direct-communications-with-your-consumers",
    ],
    displayOrder: 3,
  },
  SIGN_UP_FOR_PROMOTIONAL_AND_MARKETING: {
    label: "Data we collect when you sign up for promotional and marketing communications",
    strong: "promotional and marketing communications",
    description:
      "Where you have signed up to receive promotional and marketing communications from us, we will retain any data collected until you opt out or request its deletion. We may keep data beyond this period in anonymized form. We will further retain a record of any opt-outs in order to prevent sending you future communications.",
    suggested: [
      "name",
      "email-address",
      "postal-address",
      "purchases",
      "phone-number",
      "online-identifiers",
      "internet-activity",
      "inferences",
      "direct-communications-with-your-consumers",
    ],
    displayOrder: 4,
  },
  REVIEW_PRODUCTS_ANSWER_SURVEYS_SEND_FEEDBACK: {
    label: "Data we collect when you review our products, answer surveys, or send feedback",
    strong: "review our products, answer surveys, or send feedback",
    description:
      "We retain review, survey, and feedback data for up to YEARS following your last contact with us. We may keep data beyond this period in anonymized form to help improve our products and services.",
    suggested: ["name", "email-address", "purchases", "direct-communications-with-your-consumers"],
    displayOrder: 5,
  },
  PRIVACY_REQUESTS: {
    label: "Data we collect in connection with privacy requests",
    strong: "privacy requests",
    description:
      "We retain records related to privacy requests as long as necessary to comply with our legal obligations, and for a minimum of 24 months.",
    suggested: ["name", "email-address", "online-identifiers"],
    displayOrder: 6,
  },
  SECURITY_PURPOSES: {
    label: "Data we collect for security purposes",
    strong: "security purposes",
    description:
      "We retain security-related data as long as necessary to comply with our legal obligations and to maintain and improve our information security measures.",
    suggested: ["name", "email-address", "online-identifiers"],
    displayOrder: 7,
  },
};
